/**
 * Caminhos das telas DA CONTA.
 *
 * Essas telas ficam sob `/internal/implementation/:clientId/` para que a conta
 * apareça na URL. Antes viviam sob `/internal/admin/`, onde o contexto existia
 * só no JWT — recarregar, copiar o link ou abrir em outra aba não dizia em qual
 * conta você estava, e o prefixo `admin/` ainda as confundia com o CMS da
 * plataforma.
 *
 * As rotas antigas seguem respondendo via redirect, então links já enviados não
 * quebram; use estes helpers no código novo.
 */

/** Base de qualquer tela escopada por conta. */
export function accountPath(accountId, subPath = "") {
  const suffix = subPath ? `/${subPath.replace(/^\/+/, "")}` : "";
  return `/internal/implementation/${accountId}${suffix}`;
}

export const billingPath = (accountId) => accountPath(accountId, "admin/billing");
export const auditPath = (accountId) => accountPath(accountId, "admin/audit");
/** Exportações de canal (e, na fase 2, da conta inteira): docs/plans/PLAN_channel-and-account-export.md. */
export const exportsPath = (accountId) => accountPath(accountId, "admin/exports");
export const integrationsPath = (accountId) => accountPath(accountId, "integrations");
export const channelsPath = (accountId) => accountPath(accountId, "channels");
export const automationPath = (accountId) => accountPath(accountId, "automation");
/** Agentes do escritório: prontidão dos fluxos jurídicos (docs/plans/PLAN_legal-service-agents.md). */
export const agentsPath = (accountId) => accountPath(accountId, "agentes");
/** Contas vinculadas da PRÓPRIA conta parceira (PCA-11). O `:clientId` aqui é
 * só para a URL sobreviver ao reload e ao link compartilhado — a tenancy do
 * endpoint vem do token da sessão, nunca deste segmento. */
export const childAccountsPath = (accountId) => accountPath(accountId, "contas");

/**
 * Base de qualquer tela do shell embutido (EMB-11) — sibling de
 * `accountPath`, mas sem `:clientId` na URL: a conta vem do bearer da sessão
 * embutida (T8/T13), nunca da URL. Duas contas-filhas nunca compartilham
 * navegador — não há ambiguidade a resolver aqui.
 */
export function embedPath(subPath = "") {
  const suffix = subPath ? `/${subPath.replace(/^\/+/, "")}` : "";
  return `/embed${suffix}`;
}

/** Telas da plataforma (superadmin) — não recebem conta por natureza. */
export const PLATFORM_ROUTES = {
  plans: "/internal/platform/plans",
  content: "/internal/platform/content",
  legalDocuments: "/internal/platform/legal-documents",
  deliveryDocuments: "/internal/platform/delivery-documents",
  // Cross-account: filtra por `facets.accounts` dentro da própria tela.
  agentMetrics: "/internal/platform/agent-metrics",
  comunicados: "/internal/platform/comunicados",
  // Novidades ancoradas no menu (feature spotlight): cartão que leva a pessoa a experimentar uma funcionalidade.
  novidades: "/internal/platform/novidades",
  // Cross-account: as avaliações do Lucas vêm de todas as contas.
  lucasFeedback: "/internal/platform/avaliacoes-lucas",
  // Expediente do atendimento humano do Lucas — configuração nossa.
  lucasSchedule: "/internal/platform/atendimento-lucas",
  // Diretório de contas parceiras — slot que a Calculadora ROI ocupava.
  partners: "/internal/platform/parceiros",
  // Vera: diagnóstico de chamado. Cross-account por natureza (a conta
  // investigada é escolhida dentro da conversa), por isso vive aqui.
  vera: "/internal/platform/vera",
  // Saúde: o retrato da ronda de configuração das contas. Cross-account por
  // natureza (olha todas de uma vez), por isso vive aqui e não sob :clientId.
  saude: "/internal/platform/saude",
  // Programa "Arquitetura de Operação": fora de /platform porque a mesma rota
  // serve quem participa da turma. Para o superadmin, abre a lista de turmas.
  program: "/internal/programa",
};

/** Contas filhas de um parceiro (diretório de contas parceiras). */
export const partnerChildrenPath = (partnerId) => `${PLATFORM_ROUTES.partners}/${partnerId}/contas`;
